import { useCallback, useLayoutEffect, useRef } from "react";
import { logger } from "@/utils/logger";
import { redirectToLoginWithDestination } from "@/hooks/useRedirectToLogin";
import { trackDressDesignEvent, trackGenerateLead } from "@/utils/analytics";
import { waitForTicketSubmissionReady } from "@/utils/waitForTicketLifestyleImages";
import {
  getTicket,
  ticketAllowsCanonicalDesignUpdates,
  Ticket,
} from "@/services/ticketService";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import type { DesignSpec } from "@/types/designSpec";
import { createDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import { projectDesignSpecToLegacyFormData } from "@/lib/agent/designSpec";
import { projectSavedBackStaleness } from "@/lib/designer/previewFreshness";
import { getGeneratedFrontDesignSpec } from "@/lib/designer/savedDesign";
import { upgradeAnonymousAccount } from "@/lib/auth/anonymousSession";
import { hasVerifiedAccount } from "@/lib/auth/accountStatus";
import SavePoint from "@/utils/savePoint";
import { clearQuoteResumeAfterAuth } from "@/utils/quoteResumePersistence";
import {
  prepareQuoteAccountUpgradeHandoff,
  prepareQuoteSignInHandoff,
  QUOTE_AUTH_HANDOFF_ERROR,
} from "@/utils/quoteAuthHandoff";
import { useCreatePageStore } from "@/stores/createPageStore";
import { shouldResetForCreateIdentityTransition } from "../hooks/useCreateIdentityBoundary";
import { ActionHookContext } from "./types";

const QUOTE_SUBMISSION_ERROR_MESSAGE =
  "Your design is saved, but the quote isn't ready yet.";

function canonicalizeDesignSpec(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalizeDesignSpec);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonicalizeDesignSpec(entry)])
    );
  }
  return value;
}

function designSpecFingerprint(designSpec: DesignSpec | undefined): string {
  return JSON.stringify(canonicalizeDesignSpec(designSpec ?? null));
}

export function useTicketActions(
  context: ActionHookContext,
  updateFormData: (
    data: Parameters<typeof context.store.updateFormData>[0]
  ) => void
) {
  const {
    store,
    stateRef,
    session,
    refreshSession,
    rolesLoading,
    identityEpochRef,
    router,
    updateMutation,
    submitMutation,
    createModifiedMutation,
  } = context;
  const committedSubmissionTicketIdRef = useRef<string | null>(null);
  const committedSubmissionDesignSpecFingerprintRef = useRef<string | null>(
    null
  );
  const committedSubmissionQuoteScopeRef = useRef<"front_only" | undefined>(
    undefined
  );
  const committedSubmissionDesignSpecRef = useRef<DesignSpec | undefined>(
    undefined
  );
  const committedSubmissionImagesRef = useRef<string | null>(null);
  const pendingQuoteDesignSpecRef = useRef<DesignSpec | undefined>(undefined);
  const pendingQuoteScopeRef = useRef<"front_only" | undefined>(undefined);
  const submissionInFlightRef = useRef<{
    identityEpoch: number;
    projectActionEpoch: number;
  } | null>(null);
  const projectActionEpoch = useCreatePageStore(
    current => current.projectActionEpoch
  );
  const quoteProjectEpochRef = useRef(projectActionEpoch);
  // Quote persistence can outlive the visible dress. Retire only the old
  // project's local status/lock; its accepted server write still completes.
  const retirePreviousProjectQuote = useCallback(() => {
    const current = useCreatePageStore.getState();
    if (quoteProjectEpochRef.current === current.projectActionEpoch) return;
    quoteProjectEpochRef.current = current.projectActionEpoch;
    const previousSubmission = submissionInFlightRef.current;
    submissionInFlightRef.current = null;
    committedSubmissionTicketIdRef.current = null;
    committedSubmissionDesignSpecFingerprintRef.current = null;
    committedSubmissionQuoteScopeRef.current = undefined;
    committedSubmissionDesignSpecRef.current = undefined;
    committedSubmissionImagesRef.current = null;
    pendingQuoteDesignSpecRef.current = undefined;
    pendingQuoteScopeRef.current = undefined;
    if (previousSubmission) current.submitSuccess();
  }, []);
  useLayoutEffect(() => {
    retirePreviousProjectQuote();
  }, [projectActionEpoch, retirePreviousProjectQuote]);
  const settledIdentityRef = useRef<{
    initialized: boolean;
    userId: string | null;
  }>({ initialized: false, userId: null });

  // These refs intentionally survive ordinary renders and same-user session
  // refreshes, but none of them is transferable to another auth identity. In
  // A retry identity is never transferable to another auth user.
  useLayoutEffect(() => {
    if (rolesLoading) return;

    const userId = session?.user?.id ?? null;
    const previousIdentity = settledIdentityRef.current;
    if (!previousIdentity.initialized) {
      settledIdentityRef.current = { initialized: true, userId };
      return;
    }

    const previousUserId = previousIdentity.userId;
    settledIdentityRef.current = { initialized: true, userId };
    if (!shouldResetForCreateIdentityTransition(previousUserId, userId)) {
      return;
    }

    committedSubmissionTicketIdRef.current = null;
    committedSubmissionDesignSpecFingerprintRef.current = null;
    committedSubmissionQuoteScopeRef.current = undefined;
    committedSubmissionDesignSpecRef.current = undefined;
    committedSubmissionImagesRef.current = null;
    pendingQuoteDesignSpecRef.current = undefined;
    pendingQuoteScopeRef.current = undefined;
    submissionInFlightRef.current = null;
  }, [rolesLoading, session?.user?.id]);

  const updateGeneratedTicket = useCallback(
    async (
      ticketIdToUpdate: string,
      frontImageUrl: string,
      frontImagePath: string | null,
      backImageUrl?: string | null,
      backImagePath?: string | null,
      backPromptOverride?: string | null
    ) => {
      if (!session) return;

      const currentState = stateRef.current;

      try {
        // Use provided backPrompt if available, otherwise use form data
        const backPromptToUse =
          backPromptOverride !== undefined
            ? backPromptOverride
            : currentState.form.data.backPrompt || null;

        await updateMutation.mutateAsync({
          ticketId: ticketIdToUpdate,
          status: "generated",
          imagePath: frontImagePath || null,
          backImagePath: backImagePath || null,
          backPrompt: backPromptToUse,
        });
      } catch (error) {
        logger.error(
          "Ticket update failed with exception",
          {
            component: "CreatePageActions",
            metadata: { ticketId: ticketIdToUpdate },
          },
          error as Error
        );
        throw error;
      }
    },
    [session, stateRef, updateMutation]
  );

  const createModifiedTicket = useCallback(
    async (
      frontImageUrl: string,
      frontImagePath: string | null,
      modificationPrompt: string,
      backImageUrl?: string | null,
      backImagePath?: string | null,
      isModifyingFront?: boolean
    ): Promise<Ticket | undefined> => {
      if (!session) return;
      const identityEpoch = identityEpochRef.current;

      const currentState = stateRef.current;

      try {
        // EVERY generation/modification/duplication creates a new ticket
        // This ensures better data safety and auditability
        const currentTicketId = null; // Always create new tickets
        const designSpec = compileCurrentDesignSpec(
          currentState.agent.designBrief,
          currentState.form.data,
          {
            hasFrontImage: Boolean(currentState.generation.images.front.url),
            hasBackImage: Boolean(currentState.generation.images.back.url),
          }
        );

        const ticket = await createModifiedMutation.mutateAsync({
          formData: currentState.form.data,
          frontImageUrl,
          frontImagePath,
          modificationPrompt,
          backImageUrl: backImageUrl || undefined,
          backImagePath: backImagePath || undefined,
          isModifyingFront,
          currentTicketId,
          // Every modification mints a NEW ticket that becomes THE ticket, so
          // the session link has to be carried across or the design loses its
          // conversation from here on.
          agentSessionId: currentState.agent.sessionId,
          ...(designSpec ? { designSpec } : {}),
        });

        if (identityEpochRef.current !== identityEpoch) {
          return;
        }

        // Only update form data if ticket was successfully created
        // This ensures we only update after a modification is committed (variation selected)
        if (ticket && ticket.id) {
          store.setTicketId(ticket.id);
          // Update form data with the aggregated prompts from the ticket
          // This ensures subsequent modifications aggregate from the accumulated prompts
          // Only update if the ticket has the expected prompt fields
          if (isModifyingFront) {
            // Update front prompt with the aggregated prompt from the ticket
            if (ticket.prompt && ticket.prompt.trim()) {
              updateFormData({ prompt: ticket.prompt });
            }
          } else {
            // Update back prompt with the aggregated prompt from the ticket
            if (ticket.back_prompt && ticket.back_prompt.trim()) {
              updateFormData({ backPrompt: ticket.back_prompt });
            }
          }
        }

        return ticket;
      } catch (error) {
        if (identityEpochRef.current !== identityEpoch) {
          return;
        }
        logger.error(
          "Failed to create modified ticket",
          {
            component: "CreatePageActions",
            metadata: {
              isModifyingFront,
              hasFrontImage: !!frontImagePath,
              hasBackImage: !!backImagePath,
            },
          },
          error as Error
        );
        store.setError("We couldn't save your design. Please try again.");
        throw error;
      }
    },
    [
      session,
      store,
      createModifiedMutation,
      updateFormData,
      stateRef,
      identityEpochRef,
    ]
  );

  // Ticket submission. Location is no longer collected up front — the real
  // shipping address is gathered at checkout — so none is passed here.
  const submitTicketInternal = useCallback(
    async (
      designSpecOverride?: DesignSpec,
      options?: { skipAccountGate?: boolean; quoteScope?: "front_only" }
    ) => {
      retirePreviousProjectQuote();
      const identityEpoch = identityEpochRef.current;
      // Apply-first can continue in the render promise's microtask, before the
      // React effect synchronizes stateRef. Pair the selected contract with the
      // images/ticket that have actually been committed to the live store.
      const currentState = useCreatePageStore.getState();
      const owningProjectActionEpoch =
        useCreatePageStore.getState().projectActionEpoch;
      const isCurrentProject = () =>
        identityEpochRef.current === identityEpoch &&
        useCreatePageStore.getState().projectActionEpoch ===
          owningProjectActionEpoch;
      if (!isCurrentProject()) return false;
      // Chat capabilities and resumed quote intents can bypass the canvas
      // button. Never submit an old Back alongside a newer Front, regardless
      // of which surface requested the quote. The selected saved Front is the
      // only contract that matches the image we send in that case.
      const savedBackStale = projectSavedBackStaleness(
        currentState.generation.images
      ).stale;
      const quoteScope = savedBackStale ? "front_only" : options?.quoteScope;
      const quoteDesignSpecOverride = savedBackStale
        ? (getGeneratedFrontDesignSpec(currentState.generation.images.front) ??
          undefined)
        : designSpecOverride;
      if (quoteScope === "front_only" && !quoteDesignSpecOverride) {
        store.setError(
          "The saved front preview could not be verified for this quote."
        );
        return false;
      }

      // Prevent submission during generation states
      if (
        currentState.generation.progress.loading ||
        currentState.ui.modification.isModifying ||
        currentState.generation.images.isSelectingVariation
      ) {
        logger.debug("Submission blocked: generation in progress", {
          component: "useTicketActions",
          metadata: {
            loading: currentState.generation.progress.loading,
            isModifying: currentState.ui.modification.isModifying,
            isSelectingVariation:
              currentState.generation.images.isSelectingVariation,
          },
        });
        store.setError(
          "Wait for your design to finish updating before requesting a quote."
        );
        return false;
      }

      if (!currentState.generation.images.front.path) {
        // Log the issue for debugging
        logger.warn("Submission blocked: no front image path available", {
          component: "useTicketActions",
          metadata: {
            hasFrontUrl: !!currentState.generation.images.front.url,
            hasFrontPath: !!currentState.generation.images.front.path,
            currentStep: currentState.form.currentStep,
          },
        });
        // Provide user feedback instead of silently failing
        store.setError("Create a front preview before requesting a quote.");
        return false;
      }

      if (!session) {
        const handoff = prepareQuoteSignInHandoff({
          state: currentState,
          currentHref: window.location.href,
          designSpecOverride: quoteDesignSpecOverride,
          quoteScope,
          sourceUserId: null,
        });
        if (!handoff) {
          store.setError(QUOTE_AUTH_HANDOFF_ERROR);
          return false;
        }
        redirectToLoginWithDestination(router, handoff.returnPath);
        return false;
      }

      // Supabase anonymous users have an authenticated session, but no
      // recoverable account. A quote must belong to a real account so the
      // customer can return to it and receive follow-up. Keep the dress on
      // screen and let the global account dialog finish this same action.
      if (!hasVerifiedAccount(session.user) && !options?.skipAccountGate) {
        pendingQuoteDesignSpecRef.current =
          createDesignSpecSnapshot(quoteDesignSpecOverride) ?? undefined;
        pendingQuoteScopeRef.current = quoteScope;
        store.setShowQuoteAccountModal(true);
        return false;
      }

      // A restored quote intent and a visible quote control can become ready
      // in the same render. Serialize them in this mounted client so they
      // cannot both submit the same generated row. Cross-tab races are still
      // resolved by the database row lock and the structured 409 recovery.
      if (submissionInFlightRef.current) {
        logger.info("Skipped duplicate ticket submission while one is active", {
          component: "useTicketActions",
          metadata: { ticketId: currentState.ticket.id },
        });
        return false;
      }
      const submission = {
        identityEpoch,
        projectActionEpoch: owningProjectActionEpoch,
      };
      submissionInFlightRef.current = submission;

      try {
        store.submitStart();
        const designSpec =
          quoteDesignSpecOverride ??
          compileCurrentDesignSpec(
            currentState.agent.designBrief,
            currentState.form.data,
            {
              hasFrontImage: Boolean(currentState.generation.images.front.url),
              hasBackImage: Boolean(currentState.generation.images.back.url),
            }
          );
        const quoteIncludesBack = quoteScope !== "front_only";
        // A confirmed saved-image quote can intentionally differ from the
        // editable draft. Keep the legacy ticket fields consistent with that
        // exact contract without discarding the user's pending form edits.
        const submissionFormData = quoteDesignSpecOverride
          ? {
              ...currentState.form.data,
              ...projectDesignSpecToLegacyFormData(quoteDesignSpecOverride),
              ...(!quoteIncludesBack
                ? {
                    generateBack: false,
                    backApproved: false,
                    uploadedBackImages: [],
                    uploadedBackImageUrls: [],
                    uploadedBackImageCount: 0,
                    isBackUploadPreset: false,
                    isBackCustomPreset: false,
                  }
                : {}),
            }
          : currentState.form.data;
        const currentDesignSpecFingerprint = designSpecFingerprint(designSpec);
        const rememberedCommittedTicketId =
          committedSubmissionTicketIdRef.current;
        const currentTicketId = currentState.ticket.id;
        const rememberedTicketStillCurrent = Boolean(
          rememberedCommittedTicketId &&
          currentTicketId === rememberedCommittedTicketId
        );
        const rememberedDraftStillCurrent = Boolean(
          rememberedTicketStillCurrent &&
          committedSubmissionDesignSpecFingerprintRef.current ===
            currentDesignSpecFingerprint &&
          committedSubmissionQuoteScopeRef.current === quoteScope
        );
        // A failed quote can be retried safely on the already-committed ticket.
        // Once the customer generates/modifies into a different ticket, that
        // retry identity is stale and must never pull the old design forward.
        // A manual detail edit is equally important: submitted tickets are
        // immutable, so only an exact DesignSpec match may retry their quote
        // sidecar. An edited (or unverifiable post-reload) draft creates a new
        // ticket rather than pricing the previously committed contract.
        const committedSubmissionTicketId = rememberedDraftStillCurrent
          ? rememberedCommittedTicketId
          : null;
        const draftChangedSinceCommitted = Boolean(
          rememberedTicketStillCurrent && !rememberedDraftStillCurrent
        );
        const hasUnverifiablePersistedQuoteRetry = Boolean(
          !rememberedCommittedTicketId &&
          currentTicketId &&
          currentState.ticket.submissionError?.startsWith(
            "Your design is saved"
          )
        );
        if (
          rememberedCommittedTicketId &&
          currentTicketId &&
          currentTicketId !== rememberedCommittedTicketId
        ) {
          committedSubmissionTicketIdRef.current = null;
          committedSubmissionDesignSpecFingerprintRef.current = null;
          committedSubmissionQuoteScopeRef.current = undefined;
          committedSubmissionDesignSpecRef.current = undefined;
          committedSubmissionImagesRef.current = null;
        }

        const ticketIdForSubmission =
          draftChangedSinceCommitted || hasUnverifiablePersistedQuoteRetry
            ? null
            : (committedSubmissionTicketId ??
              (quoteIncludesBack ? currentTicketId : null));

        const ticket = await submitMutation.mutateAsync({
          ticketId: ticketIdForSubmission,
          formData: submissionFormData,
          imagePath: currentState.generation.images.front.path,
          backImagePath: quoteIncludesBack
            ? currentState.generation.images.back.path || null
            : null,
          quoteScope,
          isModificationMode: committedSubmissionTicketId
            ? false
            : currentState.ui.modification.isModificationMode,
          retryCommittedSubmission: Boolean(committedSubmissionTicketId),
          // Soft link back to the create-agent session that produced this design
          // so paid orders can be joined to their originating session.
          agentSessionId: currentState.agent.sessionId,
          ...(designSpec ? { designSpec } : {}),
        });

        if (!isCurrentProject()) {
          return false;
        }

        // Track analytics
        const imageTypeForComplete =
          currentState.form.currentStep === 2
            ? "front"
            : currentState.form.currentStep === 4
              ? "back"
              : undefined;
        trackDressDesignEvent(
          "complete",
          currentState.form.data.prompt,
          currentState.form.currentStep,
          imageTypeForComplete
        );

        // Navigate to ticket page
        const ticketId = ticket.id || currentState.ticket.id;
        if (ticketId) {
          // The ticket mutation commits before the quote sidecar runs. Keep the
          // committed id before waiting or surfacing quote recovery so Try
          // again updates this ticket even when the first path was a create.
          committedSubmissionTicketIdRef.current = ticketId;
          committedSubmissionDesignSpecFingerprintRef.current =
            currentDesignSpecFingerprint;
          committedSubmissionQuoteScopeRef.current = quoteScope;
          committedSubmissionDesignSpecRef.current =
            createDesignSpecSnapshot(designSpec) ?? undefined;
          committedSubmissionImagesRef.current = JSON.stringify([
            currentState.generation.images.front.path,
            currentState.generation.images.back.path,
          ]);
          store.setTicketId(ticketId);

          if (ticket.quotePending) {
            throw new Error(
              ticket.quoteMessage ||
                "Your design is saved, but we couldn't finish the quote yet. Please try again."
            );
          }

          // Keep the submission loader open until both the quote and its
          // lifestyle image are queryable from the ticket-data endpoint. The
          // mutation can return an approved ticket before a separately inserted
          // lifestyle row becomes visible, so even terminal quote responses
          // must pass through this readiness gate.
          const waitStartedAt = Date.now();
          logger.info("[create-page] Awaiting ticket submission readiness", {
            component: "useTicketActions",
            metadata: { ticketId },
          });
          // Use the centralized gate's 180-second default. Lifestyle image
          // generation has a 120-second model timeout, so a shorter local cap
          // would incorrectly turn slow-but-successful generations into retry
          // errors.
          const ready = await waitForTicketSubmissionReady(ticketId);
          if (!isCurrentProject()) {
            return false;
          }
          logger.info(
            "[create-page] Ticket submission readiness wait completed",
            {
              component: "useTicketActions",
              metadata: {
                ticketId,
                ready,
                elapsedMs: Date.now() - waitStartedAt,
              },
            }
          );
          if (!ready) {
            throw new Error(
              "Your design is saved, but we couldn't finish the quote yet. Please try again."
            );
          }

          // Analytics can intentionally suppress duplicate events and is also
          // disabled in local/non-production environments. It must never own
          // conversion-critical navigation.
          try {
            trackGenerateLead({ leadId: ticketId });
          } catch {}

          const quoteParams = new URLSearchParams({ guided: "instant_quote" });
          router.push(`/ticket/${ticketId}?${quoteParams.toString()}`);
        } else {
          logger.error("No ticket ID available after submission", {
            component: "useTicketActions",
          });
          store.setError(
            "Your request was submitted, but we couldn't open it. Please check My Dresses."
          );
          router.push("/requests");
          return false;
        }
        store.submitSuccess();
        return true;
      } catch (error) {
        if (!isCurrentProject()) {
          return false;
        }
        // API/database details (including invalid ticket-state transitions)
        // are useful in logs but must never become customer-facing chat copy.
        logger.warn("Quote submission needs customer retry", {
          component: "useTicketActions",
          metadata: {
            ticketId: currentState.ticket.id,
            error: error instanceof Error ? error.message : String(error),
          },
        });
        store.submitError(QUOTE_SUBMISSION_ERROR_MESSAGE);
        return false;
      } finally {
        // A retired quote must not unlock a newer dress's active submission.
        if (submissionInFlightRef.current === submission) {
          submissionInFlightRef.current = null;
        }
      }
    },
    [
      session,
      router,
      submitMutation,
      store,
      identityEpochRef,
      retirePreviousProjectQuote,
    ]
  );

  // Existing callers use the current draft; confirmed saved-image quotes
  // supply the render contract without mutating the draft.
  const getPendingQuoteDesignSpec = useCallback(() => {
    retirePreviousProjectQuote();
    return pendingQuoteDesignSpecRef.current;
  }, [retirePreviousProjectQuote]);

  const getPendingQuoteScope = useCallback(() => {
    retirePreviousProjectQuote();
    return pendingQuoteScopeRef.current;
  }, [retirePreviousProjectQuote]);

  // Public submit function keeps the optional canonical override.
  const submitTicket = useCallback(
    async (
      designSpecOverride?: DesignSpec,
      options?: { quoteScope?: "front_only" }
    ) => {
      const currentState = useCreatePageStore.getState();
      const retrySavedFrontOnlyQuote = Boolean(
        !options?.quoteScope &&
        currentState.ticket.submissionError &&
        currentState.ticket.id === committedSubmissionTicketIdRef.current &&
        committedSubmissionQuoteScopeRef.current === "front_only" &&
        committedSubmissionDesignSpecRef.current &&
        committedSubmissionImagesRef.current ===
          JSON.stringify([
            currentState.generation.images.front.path,
            currentState.generation.images.back.path,
          ])
      );
      return submitTicketInternal(
        retrySavedFrontOnlyQuote
          ? committedSubmissionDesignSpecRef.current
          : designSpecOverride,
        retrySavedFrontOnlyQuote ? { quoteScope: "front_only" } : options
      );
    },
    [submitTicketInternal]
  );

  const hideQuoteAccountModal = useCallback(() => {
    pendingQuoteDesignSpecRef.current = undefined;
    pendingQuoteScopeRef.current = undefined;
    store.setShowQuoteAccountModal(false);
  }, [store]);

  const upgradeAndSubmitQuote = useCallback(
    async (params: {
      email: string;
      password: string;
    }): Promise<{
      ok: boolean;
      error?: string;
      requiresConfirmation?: boolean;
    }> => {
      retirePreviousProjectQuote();
      const identityEpoch = identityEpochRef.current;
      // Apply-first can continue in the render promise's microtask, before the
      // React effect synchronizes stateRef. Pair the selected contract with the
      // images/ticket that have actually been committed to the live store.
      const currentState = useCreatePageStore.getState();
      const owningProjectActionEpoch =
        useCreatePageStore.getState().projectActionEpoch;
      const isCurrentProject = () =>
        identityEpochRef.current === identityEpoch &&
        useCreatePageStore.getState().projectActionEpoch ===
          owningProjectActionEpoch;
      if (!isCurrentProject()) {
        return {
          ok: false,
          error: "Your dress changed. Request the quote again.",
        };
      }
      const handoff = prepareQuoteAccountUpgradeHandoff(
        currentState,
        window.location.href,
        pendingQuoteDesignSpecRef.current,
        pendingQuoteScopeRef.current
      );
      if (!handoff) {
        return { ok: false, error: QUOTE_AUTH_HANDOFF_ERROR };
      }
      const { intentId, returnPath: confirmationReturnPath } = handoff;
      const clearIntentSavePoint = () => {
        SavePoint.clear(intentId);
      };

      const upgrade = await upgradeAnonymousAccount({
        email: params.email,
        password: params.password,
        agentSessionId: currentState.agent.sessionId,
        redirectTo: confirmationReturnPath,
      });

      if (!isCurrentProject()) {
        clearQuoteResumeAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error:
            "Your dress or session changed. Please request the quote again.",
        };
      }

      if (!upgrade.ok) {
        clearQuoteResumeAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error:
            upgrade.error === "email_taken"
              ? "That email already has an account. Sign in instead and your dress will still be here."
              : upgrade.error === "validation_failed"
                ? (upgrade.description ??
                  "Check the email and password and try again.")
                : "We couldn't create your account. Please try again.",
        };
      }

      if (upgrade.requiresConfirmation) {
        window.history.replaceState({}, "", confirmationReturnPath);
      }

      // The in-place upgrade preserves the user id that owns this dress. Sync
      // React auth state, swap dialogs, then bypass only the gate already
      // satisfied by this exact upgrade and continue the pending quote.
      await refreshSession().catch(() => {});
      if (!isCurrentProject()) {
        clearQuoteResumeAfterAuth(intentId);
        clearIntentSavePoint();
        return {
          ok: false,
          error:
            "Your dress or session changed. Please request the quote again.",
        };
      }

      if (upgrade.requiresConfirmation) {
        return { ok: true, requiresConfirmation: true };
      }

      clearQuoteResumeAfterAuth(intentId);
      clearIntentSavePoint();

      store.setShowQuoteAccountModal(false);
      const designSpecOverride = pendingQuoteDesignSpecRef.current;
      const quoteScope = pendingQuoteScopeRef.current;
      pendingQuoteDesignSpecRef.current = undefined;
      pendingQuoteScopeRef.current = undefined;
      // A no-confirmation Supabase project converts the user synchronously,
      // but this callback still closes over the pre-refresh React session.
      // Bypass only this exact invocation; the API repeats the verified-account
      // check before committing the quote.
      const submitted = await submitTicketInternal(designSpecOverride, {
        skipAccountGate: true,
        quoteScope,
      });

      if (!isCurrentProject()) {
        return {
          ok: false,
          error:
            "Your dress or session changed. Please request the quote again.",
        };
      }

      if (!submitted) {
        return {
          ok: false,
          error:
            useCreatePageStore.getState().ticket.submissionError ||
            "Your account is ready, but the quote could not be submitted. Try again.",
        };
      }

      return {
        ok: true,
        requiresConfirmation: false,
      };
    },
    [
      refreshSession,
      store,
      submitTicketInternal,
      identityEpochRef,
      retirePreviousProjectQuote,
    ]
  );

  // Sync form data to ticket when form data changes and ticket exists
  const syncFormDataToTicket = useCallback(
    async (formData: any) => {
      const currentState = stateRef.current;

      // Only sync if ticket exists and user is authenticated
      if (!currentState.ticket.id || !session) {
        return;
      }

      // Don't sync during generation or modification
      if (
        currentState.ticket.designSubmitted ||
        currentState.generation.progress.loading ||
        currentState.ui.modification.isModifying ||
        currentState.generation.images.isSelectingVariation
      ) {
        return;
      }

      try {
        const authoritativeTicket = await getTicket(currentState.ticket.id);
        if (!ticketAllowsCanonicalDesignUpdates(authoritativeTicket.status)) {
          logger.info("Skipped legacy form sync for immutable ticket", {
            component: "useTicketActions",
            metadata: {
              ticketId: currentState.ticket.id,
              ticketStatus: authoritativeTicket.status,
            },
          });
          return;
        }

        // This is only a convenience sync for legacy mutable form fields. The
        // ticket design_spec is committed at generation or quote submission as
        // the latest workflow contract, while the server-owned per-side
        // columns retain the exact contracts that produced the images.
        const updateParams: any = {
          ticketId: currentState.ticket.id,
        };

        // Only include fields that have changed or are explicitly provided
        if (formData.prompt !== undefined) {
          updateParams.prompt = formData.prompt || undefined;
        }
        if (formData.color !== undefined) {
          updateParams.color = formData.color || undefined;
        }
        if (formData.length !== undefined) {
          updateParams.length = formData.length || undefined;
        }
        if (formData.sleeve !== undefined) {
          updateParams.sleeve = formData.sleeve || undefined;
        }
        if (formData.sleeveAddons !== undefined) {
          updateParams.sleeveAddons =
            formData.sleeveAddons.length > 0
              ? formData.sleeveAddons
              : undefined;
        }
        if (formData.neckline !== undefined) {
          updateParams.neckline = formData.neckline || undefined;
        }
        if (formData.waistline !== undefined) {
          updateParams.waistline = formData.waistline || undefined;
        }
        if (formData.silhouette !== undefined) {
          updateParams.silhouette = formData.silhouette || undefined;
        }
        if (formData.fabric !== undefined) {
          updateParams.fabric = formData.fabric || undefined;
        }
        if (formData.preset_type !== undefined) {
          updateParams.preset_type = formData.preset_type || undefined;
        }
        if (formData.backPrompt !== undefined) {
          updateParams.backPrompt = formData.backPrompt || undefined;
        }

        // Only update if we have fields to update (beyond just ticketId)
        if (Object.keys(updateParams).length > 1) {
          await updateMutation.mutateAsync(updateParams);
          logger.debug("Synced form data to ticket", {
            component: "useTicketActions",
            metadata: {
              ticketId: currentState.ticket.id,
              fields: Object.keys(updateParams).filter(k => k !== "ticketId"),
            },
          });
        }
      } catch (error) {
        // Log but don't throw - form data update shouldn't block the UI
        logger.warn(
          "Failed to sync form data to ticket",
          {
            component: "useTicketActions",
            metadata: {
              ticketId: currentState.ticket.id,
              error: error instanceof Error ? error.message : String(error),
            },
          },
          error instanceof Error ? error : new Error(String(error))
        );
      }
    },
    [session, stateRef, updateMutation]
  );

  return {
    updateGeneratedTicket,
    createModifiedTicket,
    submitTicket,
    getPendingQuoteDesignSpec,
    getPendingQuoteScope,
    hideQuoteAccountModal,
    upgradeAndSubmitQuote,
    submitTicketInternal,
    syncFormDataToTicket,
  };
}
